import { useCallback, useMemo, useState } from 'react'
import { Autocomplete, Popper, styled } from '@mui/material'
import TextInput from '../../../../components/editors/input/TextInput'
import { intl } from '../../../../../i18n'
import { Program } from '../../programManagement'

type ProgramOption = {
  value: string
  label: string
}

type ProgramSelectorProps = {
  programs: Program[]
  selectedProgramUuid: string | undefined
  onSelect: (uuid: string | undefined) => void
}

const toOption = (program: Program): ProgramOption => ({
  value: program.uuid,
  label: `${program.code} - ${program.displayName}`,
})

export const ProgramSelector = ({
  programs,
  selectedProgramUuid,
  onSelect,
}: ProgramSelectorProps) => {
  const options = useMemo(() => programs.map(toOption), [programs])
  const selectedOption = useMemo(
    () => options.find(o => o.value === selectedProgramUuid) ?? null,
    [options, selectedProgramUuid]
  )
  const [inputValue, setInputValue] = useState('')

  const handleChange = useCallback(
    (_: unknown, option: ProgramOption | null) => {
      onSelect(option?.value)
    },
    [onSelect]
  )

  const handleInputChange = useCallback((_: unknown, value: string) => {
    setInputValue(value)
  }, [])

  return (
    <StyledAutocomplete
      value={selectedOption}
      options={options}
      getOptionLabel={(option: ProgramOption) => option.label}
      isOptionEqualToValue={(option: ProgramOption, value: ProgramOption) =>
        option.value === value.value
      }
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      size="small"
      PopperComponent={props => <Popper {...props} placement="bottom-start" />}
      renderInput={params => (
        <TextInput
          {...params}
          placeholder={intl.formatMessage({
            id: 'programManagement.selectProgram',
          })}
          variant="outlined"
          fullWidth={true}
          sx={{ height: '100%' }}
        />
      )}
    />
  )
}

const StyledAutocomplete = styled(Autocomplete)({
  width: '250px',
  minWidth: '250px',
  height: '30px',
  '& .MuiAutocomplete-inputRoot': {
    height: '100%',
    paddingTop: '3px !important',
  },
}) as typeof Autocomplete
